import { useState } from "react";
import { useServices, type Service } from "@/lib/queries";
import { useCreateService, useUpdateService, useDeleteService } from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash } from "lucide-react";
import { fmtINR } from "@emr/shared/india";

function ServiceFormDialog({ open, onOpenChange, service }: { open: boolean; onOpenChange: (v: boolean) => void; service?: Service | null }) {
  const [form, setForm] = useState({ name: service?.name ?? "", defaultAmountPaise: service?.defaultAmountPaise ? String(service.defaultAmountPaise) : "", category: service?.category ?? "" });
  const create = useCreateService();
  const update = useUpdateService();
  const submit = async () => {
    const body = { name: form.name, defaultAmountPaise: parseInt(form.defaultAmountPaise || "0"), category: form.category || undefined };
    try {
      if (service) await update.mutateAsync({ id: service.id, body });
      else await create.mutateAsync(body);
      onOpenChange(false);
    } catch { /* handled */ }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{service ? "Edit service" : "Add service"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Default amount (₹)</Label><Input type="number" value={form.defaultAmountPaise ? String(parseInt(form.defaultAmountPaise) / 100) : ""} onChange={(e) => setForm({ ...form, defaultAmountPaise: String(Math.round(parseFloat(e.target.value || "0") * 100)) })} /></div>
          <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Consultation, Lab" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!form.name || create.isPending || update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ServicesPage() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("services.create");
  const canEdit = isAdmin || permissions.has("services.edit");
  const canDelete = isAdmin || permissions.has("services.delete");
  const [editService, setEditService] = useState<Service | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Session counter bumps on every open so the dialog remounts fresh each time
  // and useState re-initializes from the current service prop (see SCHEMA.md §18 2026-07-20).
  const [sessionId, setSessionId] = useState(0);
  const openAdd = () => { setEditService(null); setSessionId((n) => n + 1); setAddOpen(true); };
  const openEdit = (s: Service) => { setEditService(s); setSessionId((n) => n + 1); setAddOpen(true); };
  const { data, isLoading } = useServices();
  const deleteMut = useDeleteService();
  const rows = Array.isArray(data) ? data : (data as any)?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Service Catalog</h1>
        {canCreate && <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={openAdd}><Plus size={16} className="mr-1" /> Add Service</Button>}
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500"><TableHead>Name</TableHead><TableHead>Default amount</TableHead><TableHead>Category</TableHead><TableHead className="w-20">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading ? <TableRow><TableCell colSpan={4}><Skeleton className="h-10" /></TableCell></TableRow> :
               rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted py-8">No services</TableCell></TableRow> :
               rows.map((s: Service) => (
                <TableRow key={s.id} className="even:bg-cream-50/50">
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell>{fmtINR(s.defaultAmountPaise)}</TableCell>
                  <TableCell className="text-xs text-muted">{s.category ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(s)}><Pencil size={14} /></Button>}
                      {canDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteMut.mutate(s.id)}><Trash size={14} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <ServiceFormDialog key={sessionId} open={addOpen} onOpenChange={setAddOpen} service={editService} />
    </div>
  );
}
